/**
 * The A-Roll wallet, visible where the spending happens.
 *
 * Header chip showing the workspace's two meters — AI drafts and footage
 * minutes — with the reset date on hover. White at rest, amber once either
 * meter crosses 80%, red at 100% (the transparency spec: nobody should
 * discover a limit by hitting it). Links to Settings, where the full
 * meters + plan management live.
 *
 * Refreshes on mount, on window focus, and on the `aroll:wallet-changed`
 * event that the workspace dispatches after every generation — so the
 * count ticks down as you spend, without polling.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { billing } from '@/lib/api';
import type { BillingStatus } from '@/lib/types';
import { useT } from '@/lib/aroll-i18n';

/** Event name the workspace fires after a draft generation/refine. */
export const WALLET_CHANGED_EVENT = 'aroll:wallet-changed';

export function ArollWalletChip() {
  const { t, dateLocale } = useT();
  const [status, setStatus] = useState<BillingStatus | null>(null);

  const resetLabel = (s: BillingStatus): string => {
    const iso = s.currentPeriodEnd ?? s.trialEndsAt;
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return t('wallet.resets', {
      date: d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' }),
    });
  };

  const load = useCallback(() => {
    billing
      .status()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(WALLET_CHANGED_EVENT, load);
    window.addEventListener('focus', load);
    return () => {
      window.removeEventListener(WALLET_CHANGED_EVENT, load);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  // Meters are valid with or without Stripe configured (trial workspaces
  // meter too). The chip only ever renders inside ArollFrame, which is
  // tenant-only, so the sole reason to hide is a failed status call.
  if (!status) return null;

  const { draftsUsed, draftsAllowance, minutesUsed, minutesAllowance, reviewMinutesUsed, reviewMinutesAllowance } = status.usage;
  const draftFrac = draftsAllowance > 0 ? draftsUsed / draftsAllowance : 0;
  const minuteFrac = minutesAllowance > 0 ? minutesUsed / minutesAllowance : 0;
  // Review joins the worst-fraction tone ONLY when the plan has it —
  // a Solo chip must never show 0/0 or run red over a module it
  // doesn't carry.
  const reviewFrac = reviewMinutesAllowance > 0 ? reviewMinutesUsed / reviewMinutesAllowance : 0;
  const worst = Math.max(draftFrac, minuteFrac, reviewFrac);
  const tone =
    worst >= 1
      ? 'text-[#e5664d]'
      : worst >= 0.8
        ? 'text-[#f5b83d]'
        : 'text-white/65';

  const hours = (n: number) => (n / 60).toFixed(1);

  // Plan-at-a-glance prefix: the chip is the only always-visible plan
  // surface, so it names the state, not just the meters.
  const planLabel =
    status.state === 'trial'
      ? t('wallet.trial')
      : status.state === 'active'
        ? (status.planLabel ?? null)
        : null;

  return (
    <Link
      to="/aroll/settings?section=plan"
      title={t('wallet.title', {
        draftsUsed,
        draftsAllowance,
        minutesUsed,
        minutesAllowance,
        reset: resetLabel(status),
      })}
      className={`whitespace-nowrap font-mono text-[11px] tracking-[0.04em] transition-colors hover:text-white ${tone}`}
    >
      {planLabel ? `${planLabel} · ` : ''}
      {t('wallet.meters', {
        drafts: `${draftsUsed}/${draftsAllowance}`,
        hours: `${hours(minutesUsed)}/${hours(minutesAllowance)}`,
      })}
      {reviewMinutesAllowance > 0 &&
        t('wallet.review', {
          review: `${reviewMinutesUsed}/${reviewMinutesAllowance}`,
        })}
    </Link>
  );
}
